import { inject } from '@angular/core';
import {
  HttpRequest,
  HttpContextToken,
  HttpContext,
  HttpErrorResponse,
  HttpStatusCode,
  HttpInterceptorFn
} from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { NotificationService, NotificationSeverity } from '@utils/services/notification.service';
import { SessionService } from './services/session.service';
import { ErrorRequestData } from '@utils/interfaces/error-request-data.interface';
import { ServerErrorResponse } from '@utils/interfaces/server-error.response';

/**
 * [SKIP_INTERCEPTOR]
 * @description When this context is used, the request that are sent from the
 * service that implemented the context are skipped for the interceptor
 */
const SKIP_INTERCEPTOR = new HttpContextToken<boolean>(() => false);

export function skipInterceptor() {
  return new HttpContext().set(SKIP_INTERCEPTOR, true);
}

export const AuthInterceptor: HttpInterceptorFn = (request, next) => {
  const notifications = inject(NotificationService);
  const router = inject(Router);
  const sessionService = inject(SessionService);

  if (request.context.get(SKIP_INTERCEPTOR)) {
    return next(request);
  }

  const ACCESS_TOKEN = localStorage.getItem("ACCESS_TOKEN");

  if (ACCESS_TOKEN === null) {
    return next(request).pipe(
      catchError((err: HttpErrorResponse) => {
        notificationsSwitcher(err, err.url, err.error, notifications, router, sessionService);
        return throwError(() => err);
      })
    );
  }

  let newRequest: HttpRequest<unknown> = request;

  if (ACCESS_TOKEN !== null) {
    newRequest = request.clone({
      headers: request.headers.set('Authorization', `Bearer ${ACCESS_TOKEN}`)
    });
  }

  return next(newRequest).pipe(
    catchError((err: HttpErrorResponse) => {
      notificationsSwitcher(err, err.url, err.error, notifications, router, sessionService);
      return throwError(() => err);
    })
  );

};

const notificationsSwitcher = (
  err: HttpErrorResponse,
  url: string | null,
  serverErrorResponse: ServerErrorResponse,
  notifications: NotificationService,
  router: Router,
  sessionService: SessionService
): void => {

  switch (err.status) {
    case 0: {
      notifications.create({
        severity: NotificationSeverity.error,
        summary: 'Error',
        detail: 'La respuesta tardó más de lo esperado'
      });
      break;
    }
    case HttpStatusCode.Conflict: {
      notifications.createBusinessValidation({
        detail: getErrorDetail(serverErrorResponse, 'La operación no puede completarse por una restricción del proceso.')
      });
      break;
    }
    case HttpStatusCode.Unauthorized: {
      if (sessionService.user === null) {
        notifications.create({
          severity: NotificationSeverity.error,
          summary: 'Error de Autenticación',
          detail: serverErrorResponse.detailed || 'Credenciales inválidas'
        });
        break;
      }

      sessionService.clear();

      if (url?.includes('/auth')) {
        notifications.create({
          severity: NotificationSeverity.error,
          summary: 'Sesión',
          detail: serverErrorResponse.detailed
        });
      } else {
        notifications.create({
          severity: NotificationSeverity.info,
          summary: 'Sesión',
          detail: 'Tu sesión ha expirado. Iníciala nuevamente para continuar'
        });
        router.navigateByUrl('/auth/user');
      }
      break;
    }
    default: {
      notifications.create({
        severity: NotificationSeverity.error,
        summary: `Error ${err.status}`,
        detail: getErrorDetail(serverErrorResponse, 'Ocurrió un error inesperado. Intenta nuevamente.')
      });
      break;
    }
  }
};

const getErrorDetail = (
  error: ServerErrorResponse | ErrorRequestData | unknown,
  fallback: string
): string => {
  if (error && typeof error === 'object' && 'detailed' in error) {
    const detailed = (error as ErrorRequestData).detailed;

    if (typeof detailed === 'string' && detailed.trim().length > 0) {
      return detailed;
    }
  }

  return fallback;
};
